import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from '@feathr/components';
import type { IRedirectDomainOption } from '@feathr/extender/components/RedirectDomainSelect';
import { errorMessage } from '@feathr/hooks';

import type { EmbedType } from '../DonationFormEmbedModal';

/**
 * Snippet IDs for tracking which code blocks have been copied
 */
const INLINE_SNIPPETS = ['inline-head-init', 'inline-div'] as const;
const POPUP_SNIPPETS = ['popup-head', 'popup-link'] as const;

interface IUseEmbedFormReturn {
  redirectDomainId: string | undefined;
  contentDomain: string | undefined;
  isCodeReady: boolean;
  hasAllSnippetsCopied: (embedType: EmbedType) => boolean;
  handleSelectDomain: (option: IRedirectDomainOption) => void;
  handleGenerateCode: () => Promise<void>;
  handleCodeCopied: (snippetId: string) => void;
  resetState: () => void;
}

/**
 * Custom hook for managing donation form embed code generation
 *
 * We just need the formId (from donationForm) and contentDomain (from selected domain).
 */
export function useEmbedForm(): IUseEmbedFormReturn {
  const { t } = useTranslation();
  const [redirectDomainId, setRedirectDomainId] = useState<string | undefined>(undefined);
  const [contentDomain, setContentDomain] = useState<string | undefined>(undefined);
  const [isCodeReady, setIsCodeReady] = useState<boolean>(false);
  const [copiedSnippets, setCopiedSnippets] = useState<Set<string>>(new Set());

  function handleSelectDomain({ name: label, id: value }: IRedirectDomainOption): void {
    setRedirectDomainId(value);
    setContentDomain(label);
    setCopiedSnippets(new Set());
  }

  function handleCodeCopied(snippetId: string): void {
    setCopiedSnippets((prev) => new Set(prev).add(snippetId));
  }

  /**
   * Check if all required snippets for the given embed type have been copied
   */
  function hasAllSnippetsCopied(embedType: EmbedType): boolean {
    if (!embedType) {
      return false;
    }

    const requiredSnippets = embedType === 'inline' ? INLINE_SNIPPETS : POPUP_SNIPPETS;
    return requiredSnippets.every((snippetId) => copiedSnippets.has(snippetId));
  }

  const resetState = useCallback((): void => {
    setRedirectDomainId(undefined);
    setContentDomain(undefined);
    setIsCodeReady(false);
    setCopiedSnippets(new Set());
  }, []);

  async function handleGenerateCode(): Promise<void> {
    try {
      if (!redirectDomainId) {
        throw new Error('No domain selected');
      }

      setIsCodeReady(true);

      toast(t('Embed code ready'), {
        type: 'success',
      });
    } catch (error) {
      toast(t('Failed to generate code:\n{{- error}}', { error: errorMessage(error, t) }), {
        type: 'error',
      });
    }
  }

  return {
    redirectDomainId,
    contentDomain,
    isCodeReady,
    hasAllSnippetsCopied,
    handleSelectDomain,
    handleGenerateCode,
    handleCodeCopied,
    resetState,
  };
}
